import { NS_PER_MS_BIGINT, strSecToBigIntNanosec } from "@/shared/time";

/**
 * The instant a thread renders as t=0 when its timestamps are shown relative,
 * plus enough provenance to show the reader where that instant came from.
 *
 * Anchors are per reader and per thread, and deliberately not part of the
 * thread record: how time is displayed has always been the reader's own
 * choice, and a shared anchor would let one member of an org silently change
 * what their teammates see.
 */
export interface TimeAnchor {
  /** Absolute epoch nanoseconds. Anchors are wall-clock, never relative. */
  readonly ns: bigint;
  /** Display-only provenance, e.g. "Start of ds_abc123". */
  readonly label: string;
  /** Epoch ms, used only to evict the least recently set anchor. */
  readonly setAt: number;
}

/**
 * An anchor on `ns`, stamped with the moment it was chosen. That stamp is the
 * key {@link withAnchor} evicts by, so every anchor must be built through here
 * rather than assembled field by field.
 */
export function createAnchor(ns: bigint, label: string): TimeAnchor {
  return { ns, label, setAt: Date.now() };
}

export type TimeAnchorsByThread = Readonly<Record<string, TimeAnchor>>;

/**
 * One entry accumulates per thread the reader ever anchors, so the map is
 * capped and the least recently set entries dropped. 200 is far beyond what
 * anyone revisits, and losing an old one costs a single re-pick.
 */
const MAX_STORED_ANCHORS = 200;

/** Bumped only if the stored shape changes incompatibly. */
const ANCHORS_SCHEMA_VERSION = 1;

interface StoredAnchor {
  /** Decimal string: bigint has no JSON representation. */
  ns: string;
  label: string;
  setAt: number;
}

// 19 digits spans every epoch-ns instant up to the year 2286. Anchors are
// wall-clock instants, so a negative value is corruption, never a pre-1970
// anchor worth keeping.
const EPOCH_NS_DIGITS = /^\d{1,19}$/;

/**
 * Whether `value` is a decimal string this app will read as epoch nanoseconds.
 * The single gate for every untrusted ns string — typed into the anchor
 * picker, read back out of storage, or published by the visualizer — so all
 * three reject the same values.
 */
export function isEpochNsString(value: string): boolean {
  return EPOCH_NS_DIGITS.test(value);
}

// Only the fields this build reads are checked; anything else the stored
// value carries is ignored, so an anchor written by an earlier build (which
// also persisted a `source`) still decodes.
function isStoredAnchor(value: unknown): value is StoredAnchor {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.ns === "string" &&
    EPOCH_NS_DIGITS.test(candidate.ns) &&
    typeof candidate.label === "string" &&
    typeof candidate.setAt === "number"
  );
}

/**
 * Decodes the persisted anchor map from its raw stored string. The caller
 * performs the storage read, which keeps this module usable outside a browser.
 *
 * Every failure mode — absent, unparseable, written by a schema version this
 * build does not know, or individually malformed entries — yields an empty map
 * rather than throwing, because a corrupt preference must never take the
 * thread view down with it. The next write replaces the bad value.
 */
export function decodeAnchors(raw: string | null): TimeAnchorsByThread {
  if (raw === null) {
    return {};
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== "object" || parsed === null) {
    return {};
  }
  const envelope = parsed as Record<string, unknown>;
  if (envelope.v !== ANCHORS_SCHEMA_VERSION) {
    return {};
  }
  if (typeof envelope.threads !== "object" || envelope.threads === null) {
    return {};
  }
  const decoded: Record<string, TimeAnchor> = {};
  for (const [threadId, stored] of Object.entries(
    envelope.threads as Record<string, unknown>,
  )) {
    if (!isStoredAnchor(stored)) {
      continue;
    }
    decoded[threadId] = {
      ns: BigInt(stored.ns),
      label: stored.label,
      setAt: stored.setAt,
    };
  }
  return decoded;
}

export function encodeAnchors(anchors: TimeAnchorsByThread): string {
  const threads: Record<string, StoredAnchor> = {};
  for (const [threadId, anchor] of Object.entries(anchors)) {
    threads[threadId] = {
      ns: anchor.ns.toString(),
      label: anchor.label,
      setAt: anchor.setAt,
    };
  }
  return JSON.stringify({ v: ANCHORS_SCHEMA_VERSION, threads });
}

/**
 * Returns the map with `threadId`'s anchor set, or removed when `anchor` is
 * undefined, evicting the least recently set entries past
 * {@link MAX_STORED_ANCHORS}.
 */
export function withAnchor(
  anchors: TimeAnchorsByThread,
  threadId: string,
  anchor: TimeAnchor | undefined,
): TimeAnchorsByThread {
  const next: Record<string, TimeAnchor> = { ...anchors };
  if (anchor === undefined) {
    delete next[threadId];
    return next;
  }
  next[threadId] = anchor;

  const entries = Object.entries(next);
  if (entries.length <= MAX_STORED_ANCHORS) {
    return next;
  }
  entries.sort(([, a], [, b]) => b.setAt - a.setAt);
  return Object.fromEntries(entries.slice(0, MAX_STORED_ANCHORS));
}

const FIXED_POINT_SECONDS = /^\d+\.\d+$/;
const ISO_8601_PREFIX = /^\d{4}-\d{2}-\d{2}/;

/**
 * Parses a time typed into the anchor picker into epoch nanoseconds, or
 * undefined when it is none of the three accepted shapes: bare digits are
 * epoch NANOSECONDS (what Roboto's own links and tool output carry), digits
 * either side of a decimal point are fixed-point epoch seconds, and anything
 * opening with an ISO 8601 date goes to `Date.parse`.
 *
 * Each shape needs its own pattern rather than falling through to
 * `Date.parse`, whose legacy parsing reads short punctuation-led numbers
 * ("-5", "+5", ".5") as years and would silently anchor the thread to 2001.
 *
 * Bare digits meaning nanoseconds does mean a pasted epoch-seconds value
 * resolves to 1970 instead of being rejected. The picker shows the parsed
 * instant before the reader commits, which is what makes that legible.
 */
export function parseAnchorInput(text: string): bigint | undefined {
  const trimmed = text.trim();
  if (EPOCH_NS_DIGITS.test(trimmed)) {
    return BigInt(trimmed);
  }
  if (FIXED_POINT_SECONDS.test(trimmed)) {
    return strSecToBigIntNanosec(trimmed);
  }
  if (!ISO_8601_PREFIX.test(trimmed)) {
    return undefined;
  }
  const parsedMs = Date.parse(trimmed);
  return Number.isNaN(parsedMs)
    ? undefined
    : BigInt(parsedMs) * NS_PER_MS_BIGINT;
}
